/*
 * Interactive ingredient explorer (<jc-ingredients-section>).
 *
 * Shared by the PDP "What's in every bowl" section (sections/jc-ingredients
 * .liquid — one meal, keyed by the product's custom.ingredients metafield)
 * and the Our Food page (sections/jc-our-food.liquid — every ingredient
 * across all meals, via data-meal-key="__all__").
 *
 * Reads assets/jc-ingredients.json (data.meals[<key>] = { ingredients,
 * nutrition }). The asset-URL manifest is injected server-side as a
 * <script data-asset-map> so JS gets versioned CDN URLs per thumbnail.
 */
(function () {
  // Sentinel meal key: build a synthetic "meal" that is the deduped union
  // of every ingredient across all meals (the Our Food explorer).
  var ALL_KEY = '__all__';

  // The Our Food explorer groups ingredients into categories, each with a
  // colour from the brand meal palette: meat = coral, seafood = blue, veg =
  // green, grains/legumes = yellow, seeds/nuts = olive, superfoods/supplements
  // = orange. Each category bar is in that colour.
  // Ingredients are keyed by image slug; anything unlisted falls in 'extras'.
  // CATEGORIES sets the accordion order and names; each bar's image is
  // assets/ingredient-category-<key>.webp (passed in via data-category-images).
  var CATEGORIES = [
    { key: 'meat', name: 'Meat and Poultry' },
    { key: 'seafood', name: 'Seafood' },
    { key: 'veg', name: 'Vegetables and Greens' },
    { key: 'grains', name: 'Grains and Legumes' },
    { key: 'seeds', name: 'Seeds and Nuts' },
    { key: 'extras', name: 'Superfoods and Supplements' }
  ];
  var CATEGORY_COLOURS = {
    meat: '#F58B7B',
    seafood: '#7098CF',
    veg: '#3A6942',
    grains: '#FFC612',
    seeds: '#989E37',
    extras: '#F89622'
  };
  var INGREDIENT_CATEGORY = {
    // Meat & poultry (incl. organ meats)
    'beef': 'meat', 'beef-heart': 'meat', 'beef-liver': 'meat',
    'chicken-breast-and-thigh': 'meat', 'chicken-gizzards': 'meat',
    'chicken-heart': 'meat', 'chicken-liver': 'meat',
    'lamb-meat': 'meat', 'lamb-heart': 'meat', 'lamb-kidney': 'meat', 'lamb-liver': 'meat',
    'wallaby-meat': 'meat',
    // Seafood
    'salmon': 'seafood', 'sardines': 'seafood', 'blue-grenadier': 'seafood',
    'oysters': 'seafood', 'green-lipped-mussels': 'seafood',
    'cod-liver-oil': 'seafood', 'seaweed': 'seafood',
    // Vegetables & greens
    'beetroot': 'veg', 'broccoli': 'veg', 'carrot': 'veg', 'cauliflower': 'veg',
    'celery': 'veg', 'kale': 'veg', 'parsley': 'veg', 'spinach': 'veg',
    'sweet-potato': 'veg', 'wheatgrass-powder': 'veg',
    // Grains & legumes
    'brown-rice': 'grains', 'chickpeas': 'grains', 'green-peas': 'grains', 'lentils': 'grains',
    // Seeds, nuts & oils
    'almonds': 'seeds', 'chia-seeds': 'seeds', 'hemp-seeds': 'seeds',
    'linseeds': 'seeds', 'pepitas': 'seeds', 'sunflower-seed': 'seeds',
    // Superfoods, supplements & extras
    'blueberries': 'extras', 'bone-broth': 'extras', 'free-range-eggs': 'extras',
    'nutritional-yeast': 'extras', 'vitamins-and-minerals': 'extras',
    'calcium-carbonate': 'extras', 'eggshell-powder': 'extras', 'mcha-powder': 'extras',
    'sea-salt': 'extras', 'black-pepper': 'extras', 'turmeric': 'extras'
  };

  class JCIngredientsSection extends HTMLElement {
    connectedCallback() {
      if (this._inited) return;
      this._inited = true;

      this.mealKey = this.dataset.mealKey;
      if (!this.mealKey) return;  // non-meal product — leave shell hidden
      // 'cards' = the Our Food explorer (category accordion of card grids +
      // expand-below-row); 'panel' = the PDP list + hover/click side panel.
      this.layout = this.dataset.layout === 'cards' ? 'cards' : 'panel';
      this.shell = this.querySelector('[data-shell]');

      try {
        const mapNode = this.querySelector('[data-asset-map]');
        this.assetMap = JSON.parse(mapNode.textContent || '{}');
      } catch (e) {
        console.error('jc-ingredients: bad asset map JSON', e);
        return;
      }

      if (this.layout === 'cards') {
        this.cardsEl = this.querySelector('[data-ingredient-cards]');
        this.openIndex = null;
      } else {
        this.listEl = this.querySelector('[data-ingredient-list]');
        this.benefitsGrid = this.querySelector('[data-benefits-grid]');
        this.benefitsEyebrow = this.querySelector('[data-benefits-eyebrow]');
        this.eyebrowText = this.querySelector('[data-eyebrow-text]');
        this.eyebrowIcon = this.querySelector('[data-eyebrow-icon]');
        this.introEl = this.querySelector('[data-ingredient-intro]');
        this.introSummary = this.querySelector('[data-intro-summary]');
        this.introSource = this.querySelector('[data-intro-source]');
        this.nutritionEl = this.querySelector('[data-nutrition]');
        this.nutritionProtein = this.querySelector('[data-nutrition-protein]');
        this.nutritionFat = this.querySelector('[data-nutrition-fat]');
        this.nutritionCalories = this.querySelector('[data-nutrition-calories]');
        this.defaultEyebrow = this.eyebrowText.textContent.trim();
        this.mq = window.matchMedia('(min-width: 750px)');
        // Active = currently shown in the side panel. Pinned = clicked, persists
        // when the customer hovers elsewhere; hovered transiently overrides it.
        this.hoveredIndex = null;
        this.pinnedIndex = null;
      }

      this.loadConfig().then((meal) => {
        if (!meal) return;
        this.meal = meal;
        this.shell.hidden = false;
        if (this.layout === 'cards') {
          this.renderCards();
        } else {
          this.renderNutrition();
          this.renderIngredients();
          this.renderBenefits(this.unionBenefits(), false);
        }
      });

      // Click outside the section: unpin (panel) / close the open card (cards).
      document.addEventListener('click', (e) => {
        if (this.contains(e.target)) return;
        if (this.layout === 'cards') {
          if (this.openIndex !== null) this.closeCard();
        } else {
          if (this.pinnedIndex === null) return;
          this.pinnedIndex = null;
          this.refreshDesktop();
        }
      });
    }

    async loadConfig() {
      try {
        const res = await fetch(this.dataset.configUrl, { credentials: 'omit' });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const data = await res.json();
        const meals = (data && data.meals) || {};
        if (this.mealKey === ALL_KEY) return this.buildAllMeal(meals);
        return meals[this.mealKey] || null;
      } catch (e) {
        console.error('jc-ingredients: failed to load config', e);
        return null;
      }
    }

    // Consolidated explorer: every ingredient across all meals, deduped by
    // name (first occurrence wins — the same ingredient carries identical
    // data in every meal), sorted alphabetically so a 50-item list stays
    // easy to scan. No nutrition (it is per-meal).
    buildAllMeal(meals) {
      const seen = new Set();
      const ingredients = [];
      Object.keys(meals).forEach((key) => {
        const m = meals[key];
        if (!m || !m.ingredients) return;
        m.ingredients.forEach((ing) => {
          const id = String(ing.name || '').trim().toLowerCase();
          if (!id || seen.has(id)) return;
          seen.add(id);
          ingredients.push(ing);
        });
      });
      ingredients.sort((a, b) => String(a.name).localeCompare(String(b.name)));
      return { name: 'All ingredients', ingredients: ingredients, nutrition: null };
    }

    // Union of every ingredient's benefits, preserving the order they
    // first appear. Dedupes by icon (keeping the first label seen) so
    // the grid stays compact even when the same icon is reused for
    // different reword variants — e.g. "Lean protein" vs "Plant-based
    // protein" both collapse to one Protein cell with the first label.
    unionBenefits() {
      const seenIcons = new Set();
      const out = [];
      for (const ing of this.meal.ingredients) {
        for (const b of ing.benefits) {
          if (seenIcons.has(b.icon)) continue;
          seenIcons.add(b.icon);
          out.push(b);
        }
      }
      return out;
    }

    renderIngredients() {
      this.listEl.innerHTML = '';

      // Container-level leave handlers — fire only when the cursor /
      // focus exits the list entirely, so moving between adjacent
      // items doesn't briefly clear the side panel.
      this.listEl.addEventListener('mouseleave', () => this.setHover(null));
      this.listEl.addEventListener('focusout', (e) => {
        if (!this.listEl.contains(e.relatedTarget)) this.setHover(null);
      });

      // Static hint row at the top of the list. Non-interactive — its
      // job is to tell the customer the rows below are clickable.
      const hint = document.createElement('li');
      hint.className = 'jc-ingredients__item jc-ingredients__item--hint';
      hint.textContent = 'Hover or click on any ingredient to learn more';
      this.listEl.appendChild(hint);

      this.meal.ingredients.forEach((ing, i) => {
        const li = document.createElement('li');
        li.className = 'jc-ingredients__item';
        li.dataset.index = String(i);
        li.tabIndex = 0;

        const imgUrl = this.assetMap['ingredient/' + ing.image] || '';
        // Mobile inline panel — intro (summary + source) above the
        // benefits list, mirroring the desktop active-panel layout.
        const introMobile =
          (ing.summary || ing.sourced_from)
            ? '<div class="jc-ingredients__intro jc-ingredients__intro--inline">' +
                (ing.summary
                  ? '<p class="jc-ingredients__intro-summary">' + this.escape(ing.summary) + '</p>'
                  : '') +
                (ing.sourced_from
                  ? '<p class="jc-ingredients__intro-source"><strong>Sourced from:</strong> ' +
                    this.escape(ing.sourced_from) + this.flagFor(ing.sourced_from) + '</p>'
                  : '') +
              '</div>'
            : '';

        li.innerHTML =
          '<button type="button" class="jc-ingredients__item-button" aria-expanded="false">' +
            '<img class="jc-ingredients__item-thumb" src="' + imgUrl + '" alt="" loading="lazy" width="80" height="80">' +
            '<span class="jc-ingredients__item-name">' + this.escape(ing.name) + '</span>' +
            '<span class="jc-ingredients__item-chevron" aria-hidden="true">+</span>' +
            '<span class="jc-ingredients__item-reset" aria-hidden="true" title="Reset to all benefits">' +
              '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round">' +
                '<line x1="3" y1="3" x2="13" y2="13" />' +
                '<line x1="13" y1="3" x2="3" y2="13" />' +
              '</svg>' +
            '</span>' +
          '</button>' +
          '<div class="jc-ingredients__item-detail" data-item-detail hidden>' +
            introMobile +
            '<ul class="jc-ingredients__item-benefits"></ul>' +
          '</div>';

        // Mobile inline-benefits panel (per ingredient row).
        const inline = li.querySelector('.jc-ingredients__item-benefits');
        ing.benefits.forEach((b) => inline.appendChild(this.buildBenefitNode(b)));

        // Desktop hover/focus is transient — it previews benefits but
        // doesn't persist after mouseout / blur. Click pins.
        // Mouseleave/focusout are bound to the list container instead
        // of per-item so transitions between adjacent items don't flash
        // back to the union state.
        li.addEventListener('mouseenter', () => this.setHover(i));
        li.addEventListener('focus', () => this.setHover(i));

        li.querySelector('button').addEventListener('click', (e) => {
          if (!this.mq.matches) {
            // Mobile — accordion expand/collapse, no pinning.
            e.preventDefault();
            this.toggleMobileRow(li);
            return;
          }
          // Desktop — toggle pin (click pinned item again to unpin).
          e.preventDefault();
          this.pinnedIndex = (this.pinnedIndex === i) ? null : i;
          this.refreshDesktop();
        });

        this.listEl.appendChild(li);
      });
    }

    setHover(index) {
      if (!this.mq.matches) return;
      this.hoveredIndex = index;
      this.refreshDesktop();
    }

    // Decides what's currently shown in the side panel: hover wins if
    // the customer is currently over an item, otherwise the pinned
    // item, otherwise the union of all benefits.
    refreshDesktop() {
      if (!this.mq.matches) return;
      const showIndex = (this.hoveredIndex !== null && this.hoveredIndex !== undefined)
        ? this.hoveredIndex
        : this.pinnedIndex;

      // Skip the hint row at the top of the list — its DOM index
      // would otherwise shift every real ingredient by one and the
      // active/pinned classes would land on the wrong row.
      const items = this.listEl.querySelectorAll('.jc-ingredients__item:not(.jc-ingredients__item--hint)');
      items.forEach((el, i) => {
        el.classList.toggle('is-active', i === showIndex);
        el.classList.toggle('is-pinned', i === this.pinnedIndex);
      });

      if (showIndex === null || showIndex === undefined) {
        this.eyebrowText.textContent = this.defaultEyebrow;
        this.eyebrowIcon.hidden = true;
        this.eyebrowIcon.removeAttribute('src');
        this.setIntro(null);
        this.renderBenefits(this.unionBenefits(), false);
      } else {
        const ing = this.meal.ingredients[showIndex];
        this.eyebrowText.textContent = ing.name;
        const iconUrl = this.assetMap['ingredient/' + ing.image] || '';
        if (iconUrl) {
          this.eyebrowIcon.src = iconUrl;
          this.eyebrowIcon.hidden = false;
        } else {
          this.eyebrowIcon.hidden = true;
        }
        this.setIntro(ing);
        this.renderBenefits(ing.benefits, true);
      }
    }

    renderNutrition() {
      if (!this.nutritionEl) return;
      const n = this.meal.nutrition;
      if (!n || (!n.protein && !n.fat && !n.calories)) {
        this.nutritionEl.hidden = true;
        return;
      }
      this.nutritionProtein.textContent = n.protein || '';
      this.nutritionFat.textContent = n.fat || '';
      this.nutritionCalories.textContent = n.calories || '';
      this.nutritionEl.hidden = false;
    }

    // Show the general per-ingredient summary + sourcing line above
    // the benefits list. Hidden in the union ('All benefits') view.
    setIntro(ing) {
      if (!this.introEl) return;
      if (!ing || (!ing.summary && !ing.sourced_from)) {
        this.introEl.hidden = true;
        return;
      }
      this.introEl.hidden = false;
      this.introSummary.textContent = ing.summary || '';
      this.introSummary.hidden = !ing.summary;
      // innerHTML (not textContent) because flagFor returns an <img>
      // fragment. Source name is HTML-escaped before concatenation.
      this.introSource.innerHTML = ing.sourced_from
        ? this.escape(ing.sourced_from) + this.flagFor(ing.sourced_from)
        : '';
      // Hide the entire "Sourced from:" line if the field is blank.
      this.introSource.parentElement.hidden = !ing.sourced_from;
    }

    toggleMobileRow(li) {
      const willOpen = !li.classList.contains('is-open');
      // Accordion: only one ingredient open at a time — collapse any other
      // open row before toggling this one.
      const rows = li.parentElement ? li.parentElement.querySelectorAll('li.is-open') : [];
      rows.forEach((row) => {
        if (row === li) return;
        row.classList.remove('is-open');
        const ob = row.querySelector('button');
        const op = row.querySelector('[data-item-detail]');
        if (ob) ob.setAttribute('aria-expanded', 'false');
        if (op) op.hidden = true;
      });
      li.classList.toggle('is-open', willOpen);
      const btn = li.querySelector('button');
      const panel = li.querySelector('[data-item-detail]');
      btn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      panel.hidden = !willOpen;
    }

    renderBenefits(benefits, isFiltered) {
      this.benefitsGrid.innerHTML = '';
      benefits.forEach((b) => this.benefitsGrid.appendChild(this.buildBenefitNode(b)));
      // Layout switches to a vertical list with full descriptions
      // when an ingredient is active; defaults to a compact grid for
      // the union view.
      this.benefitsGrid.classList.toggle('is-list', !!isFiltered);
    }

    buildBenefitNode(benefit) {
      const li = document.createElement('li');
      li.className = 'jc-ingredients__benefit';
      const url = this.assetMap['benefit/' + benefit.icon] || '';
      const desc = benefit.description
        ? '<p class="jc-ingredients__benefit-description">' + this.escape(benefit.description) + '</p>'
        : '';
      // Description is always rendered; CSS hides it in the default
      // grid view and reveals it when the grid switches to .is-list.
      li.innerHTML =
        '<img class="jc-ingredients__benefit-icon" src="' + url + '" alt="" loading="lazy" width="60" height="60">' +
        '<div class="jc-ingredients__benefit-text">' +
          '<span class="jc-ingredients__benefit-label">' + this.escape(benefit.label) + '</span>' +
          desc +
        '</div>';
      return li;
    }

    // ---------- Cards layout (Our Food explorer) ----------
    // A vertical accordion of ingredient categories. Each category is a bar in
    // its colour (name, representative image, +/-); opening it reveals a grid
    // of that category's ingredient cards (name + summary + image). Clicking a
    // card opens a single full-width detail panel inserted directly after
    // that card's row, with the sourcing line + benefits.
    renderCards() {
      this.cardsEl.innerHTML = '';
      this.cardNodes = [];

      let images = {};
      try {
        const node = this.querySelector('[data-category-images]');
        images = node ? JSON.parse(node.textContent || '{}') : {};
      } catch (e) {
        console.error('jc-ingredients: bad category images JSON', e);
      }

      // Ingredient indexes per category, keeping the alphabetical order.
      const groups = {};
      this.meal.ingredients.forEach((ing, i) => {
        const key = INGREDIENT_CATEGORY[ing.image] || 'extras';
        (groups[key] = groups[key] || []).push(i);
      });

      CATEGORIES.forEach((cat) => {
        const indexes = groups[cat.key];
        if (!indexes) return;
        const panelId = 'jc-foodcat-' + cat.key;
        const group = document.createElement('div');
        group.className = 'jc-foodcats__group';
        group.style.setProperty('--card-accent', CATEGORY_COLOURS[cat.key]);
        group.innerHTML =
          '<h3 class="jc-foodcats__heading">' +
            '<button type="button" class="jc-foodcats__toggle" aria-expanded="false" aria-controls="' + panelId + '">' +
              (images[cat.key] ? '<img class="jc-foodcats__img" src="' + images[cat.key] + '" alt="" loading="lazy">' : '') +
              '<span class="jc-foodcats__name">' + this.escape(cat.name) + '</span>' +
              '<span class="jc-foodcats__plus" aria-hidden="true"></span>' +
            '</button>' +
          '</h3>' +
          '<div class="jc-foodcats__panel" id="' + panelId + '" hidden>' +
            '<div class="jc-foodcards"></div>' +
          '</div>';
        const grid = group.querySelector('.jc-foodcards');
        indexes.forEach((i) => grid.appendChild(this.buildCard(i)));
        group.querySelector('.jc-foodcats__toggle').addEventListener('click', (e) => {
          e.preventDefault();
          this.toggleCategory(group);
        });
        this.cardsEl.appendChild(group);
      });

      // One reusable detail panel, moved to the right row when a card opens.
      this.detail = document.createElement('div');
      this.detail.className = 'jc-foodcards__detail';
      this.detail.hidden = true;

      // The row a card belongs to depends on the column count, so re-place the
      // open panel when the grid reflows.
      window.addEventListener('resize', () => {
        if (this.openIndex !== null) this.placeDetail(this.openIndex);
      });
    }

    // One ingredient card.
    buildCard(i) {
      const ing = this.meal.ingredients[i];
      const thumb = this.assetMap['ingredient/' + ing.image] || '';
      // The asset map carries the 80px thumb; the full-size image shares the
      // same URL without the "-thumb" suffix.
      const full = thumb.replace('-thumb.png', '.png');
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'jc-foodcards__card';
      card.dataset.index = String(i);
      card.setAttribute('aria-expanded', 'false');
      card.innerHTML =
        '<span class="jc-foodcards__media">' +
          '<span class="jc-foodcards__plus" aria-hidden="true"></span>' +
          (full ? '<img class="jc-foodcards__img" src="' + full + '" alt="" loading="lazy">' : '') +
        '</span>' +
        '<span class="jc-foodcards__name">' + this.escape(ing.name) + '</span>' +
        (ing.summary ? '<p class="jc-foodcards__text">' + this.escape(ing.summary) + '</p>' : '');
      card.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleCard(i);
      });
      this.cardNodes[i] = card;
      return card;
    }

    // Open or close one category. Only one is open at a time, so opening a
    // category closes any other.
    toggleCategory(group) {
      const btn = group.querySelector('.jc-foodcats__toggle');
      const willOpen = btn.getAttribute('aria-expanded') !== 'true';
      this.cardsEl.querySelectorAll('.jc-foodcats__group').forEach((g) => {
        this.setCategoryOpen(g, willOpen && g === group);
      });
      // Closing a longer category above pulls this bar up the page, possibly
      // under the sticky header; bring it back into view (scroll-margin-top
      // in the CSS clears the header).
      if (willOpen) {
        const clearance = parseFloat(getComputedStyle(group).scrollMarginTop) || 0;
        if (group.getBoundingClientRect().top < clearance) {
          const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          group.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
        }
      }
    }

    // Closing a category also closes an ingredient detail panel open inside it.
    setCategoryOpen(group, open) {
      const btn = group.querySelector('.jc-foodcats__toggle');
      const panel = group.querySelector('.jc-foodcats__panel');
      if (!open && this.openIndex !== null && panel.contains(this.cardNodes[this.openIndex])) {
        this.closeCard();
      }
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      group.classList.toggle('is-open', open);
      panel.hidden = !open;
    }

    toggleCard(i) {
      if (this.openIndex === i) { this.closeCard(); return; }
      this.openIndex = i;
      this.fillDetail(i);
      this.placeDetail(i);
      this.detail.hidden = false;
      this.cardNodes.forEach((c, idx) => {
        const on = idx === i;
        c.classList.toggle('is-open', on);
        c.setAttribute('aria-expanded', on ? 'true' : 'false');
      });
    }

    closeCard() {
      this.openIndex = null;
      this.detail.hidden = true;
      if (this.detail.parentNode) this.detail.parentNode.removeChild(this.detail);
      this.cardNodes.forEach((c) => {
        c.classList.remove('is-open');
        c.setAttribute('aria-expanded', 'false');
      });
    }

    fillDetail(i) {
      const ing = this.meal.ingredients[i];
      const source = ing.sourced_from
        ? '<p class="jc-foodcards__detail-source"><strong>Sourced from:</strong> ' +
            this.escape(ing.sourced_from) + this.flagFor(ing.sourced_from) + '</p>'
        : '';
      const benefits = (ing.benefits || []).map((b) => {
        const url = this.assetMap['benefit/' + b.icon] || '';
        return '<li class="jc-foodcards__benefit">' +
          (url ? '<img class="jc-foodcards__benefit-icon" src="' + url + '" alt="" loading="lazy" width="80" height="80">' : '') +
          '<div class="jc-foodcards__benefit-text">' +
            '<span class="jc-foodcards__benefit-label">' + this.escape(b.label) + '</span>' +
            (b.description ? '<p class="jc-foodcards__benefit-desc">' + this.escape(b.description) + '</p>' : '') +
          '</div></li>';
      }).join('');

      this.detail.innerHTML =
        '<div class="jc-foodcards__detail-inner">' +
          '<button type="button" class="jc-foodcards__detail-close" aria-label="Close">' +
            '<svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><line x1="3" y1="3" x2="13" y2="13"/><line x1="13" y1="3" x2="3" y2="13"/></svg>' +
          '</button>' +
          '<h4 class="jc-foodcards__detail-name">' + this.escape(ing.name) + '</h4>' +
          source +
          '<ul class="jc-foodcards__detail-benefits">' + benefits + '</ul>' +
        '</div>';

      this.detail.querySelector('.jc-foodcards__detail-close')
        .addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); this.closeCard(); });
    }

    // Insert the detail panel right after the last card in i's row of its
    // category grid, so it spans the full width below that row (column count
    // read from the live grid).
    placeDetail(i) {
      const card = this.cardNodes[i];
      const grid = card && card.parentNode;
      if (!grid) return;
      const cards = Array.prototype.slice.call(grid.querySelectorAll('.jc-foodcards__card'));
      const pos = cards.indexOf(card);
      const cols = this.getColumns(grid);
      const ref = cards[Math.min(Math.floor(pos / cols) * cols + (cols - 1), cards.length - 1)];
      if (ref && ref.nextSibling !== this.detail) {
        grid.insertBefore(this.detail, ref.nextSibling);
      }
    }

    getColumns(grid) {
      const tpl = getComputedStyle(grid).gridTemplateColumns || '';
      return Math.max(1, tpl.split(' ').filter(Boolean).length);
    }

    escape(s) {
      return String(s).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
      }[c]));
    }

    // Map a sourced_from value to a Twemoji flag image. Native flag
    // emoji are rendered as letter pairs (e.g. "AU") on Windows
    // Chrome/Edge, so we use Twemoji SVGs from cdnjs to guarantee
    // identical flags on every OS. Australian state names + 'Mainland
    // Australia' roll up to the AU flag; 'Assorted Locations' /
    // 'To be confirmed' return ''. Returns an HTML fragment — caller
    // must use innerHTML, not textContent. ISO code in alt text gives
    // a fallback for screen readers + if the SVG fails to load.
    flagFor(source) {
      // Codepoints are the regional-indicator pair for each flag.
      const FLAGS = {
        // Australia (national + state-level entries)
        'Tasmania':                       ['1f1e6-1f1fa', 'AU'],
        'Victoria':                       ['1f1e6-1f1fa', 'AU'],
        'New South Wales':                ['1f1e6-1f1fa', 'AU'],
        'Queensland':                     ['1f1e6-1f1fa', 'AU'],
        'South Australia':                ['1f1e6-1f1fa', 'AU'],
        'Western Australia':              ['1f1e6-1f1fa', 'AU'],
        'Northern Territory':             ['1f1e6-1f1fa', 'AU'],
        'Australian Capital Territory':   ['1f1e6-1f1fa', 'AU'],
        'Mainland Australia':             ['1f1e6-1f1fa', 'AU'],
        'Australia':                      ['1f1e6-1f1fa', 'AU'],
        // Other countries
        'New Zealand':                    ['1f1f3-1f1ff', 'NZ'],
        'India':                          ['1f1ee-1f1f3', 'IN'],
        'Iceland':                        ['1f1ee-1f1f8', 'IS'],
        'China':                          ['1f1e8-1f1f3', 'CN'],
        'Belgium':                        ['1f1e7-1f1ea', 'BE'],
        'United States of America':       ['1f1fa-1f1f8', 'US'],
        'USA':                            ['1f1fa-1f1f8', 'US'],
      };
      const entry = FLAGS[source];
      if (!entry) return '';
      const code = entry[0];
      const iso = entry[1];
      const url = 'https://cdnjs.cloudflare.com/ajax/libs/twemoji/14.0.2/svg/' + code + '.svg';
      return ' <img class="jc-flag" src="' + url + '" alt="' + iso + '" loading="lazy" decoding="async">';
    }
  }

  if (!customElements.get('jc-ingredients-section')) {
    customElements.define('jc-ingredients-section', JCIngredientsSection);
  }
})();
